import { useRef, useEffect, useState, useCallback } from 'react'
import {
  useDashboardStore,
  selectTitle,
  selectSummary,
  selectBlocks,
  selectStatus,
  selectError,
  selectNodeExecutions,
  selectCurrentMessageId,
} from '@/store/dashboardStore'
import type { ChatMessage } from '@/types/chat'
import { UserMessageBubble } from './UserMessageBubble'
import { AssistantMessageBubble } from './AssistantMessageBubble'
import { EmptyState } from './EmptyState'
import { ScrollToBottomButton } from './ScrollToBottomButton'

interface ChatMessageListProps {
  messages: ChatMessage[]
}

/**
 * Scrollable message list with Claude/ChatGPT-style auto-scroll.
 *
 * Scroll behavior:
 * 1. "Sticky bottom" — RAF loop pins scrollTop to max as content streams in.
 * 2. User scrolls up (wheel/touch) → sticky breaks, ↓ button appears.
 * 3. Click ↓ → smooth scroll to bottom, re-engage sticky.
 * 4. New message submitted → smooth scroll to show user bubble + thinking.
 * 5. Streaming completes → show ↓ button (no auto-scroll). User clicks to go bottom.
 */
export function ChatMessageList({ messages }: ChatMessageListProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // "Sticky" flag — ref (not state) to avoid re-renders during streaming
  const stickyRef = useRef(true)

  // Whether to show the ↓ button — state because it drives UI rendering
  const [showScrollBtn, setShowScrollBtn] = useState(false)

  // Track previous scroll height to detect content growth
  const prevScrollHeightRef = useRef(0)

  // RAF handle for the sticky-scroll loop
  const rafRef = useRef<number | null>(null)

  // Live streaming state from store root (for current live message)
  const currentMessageId = useDashboardStore(selectCurrentMessageId)
  const title = useDashboardStore(selectTitle)
  const summary = useDashboardStore(selectSummary)
  const blocks = useDashboardStore(selectBlocks)
  const status = useDashboardStore(selectStatus)
  const error = useDashboardStore(selectError)
  const nodeExecutions = useDashboardStore(selectNodeExecutions)
  const durationMs = useDashboardStore((state) => state.durationMs)

  const liveState = {
    title,
    summary,
    blocks,
    nodeExecutions,
    status,
    error,
    durationMs,
  }

  // ============================================================================
  // Sticky-scroll RAF loop
  // Runs whenever a live message exists (currentMessageId is set) — not just
  // during 'streaming'. This covers the thinking indicator phase too.
  // ============================================================================
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    // Start the RAF loop when there is an active live message
    const shouldRun = !!currentMessageId

    if (!shouldRun) return

    prevScrollHeightRef.current = container.scrollHeight

    const tick = () => {
      if (stickyRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = container
        const maxScroll = scrollHeight - clientHeight

        // Only scroll if content actually grew (new DOM content appeared)
        if (scrollHeight > prevScrollHeightRef.current && scrollTop < maxScroll) {
          container.scrollTop = maxScroll
        }
        prevScrollHeightRef.current = scrollHeight
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [currentMessageId])

  // ============================================================================
  // User-scroll detection
  // Wheel / touch → check if user scrolled away from bottom.
  // ============================================================================
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const handleUserScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container
      const distFromBottom = scrollHeight - scrollTop - clientHeight

      if (distFromBottom > 120) {
        // User scrolled up — break sticky, show ↓ button
        stickyRef.current = false
        setShowScrollBtn(true)
      } else {
        // Near bottom — re-engage sticky, hide button
        stickyRef.current = true
        setShowScrollBtn(false)
      }
    }

    container.addEventListener('wheel', handleUserScroll, { passive: true })
    container.addEventListener('touchmove', handleUserScroll, { passive: true })

    return () => {
      container.removeEventListener('wheel', handleUserScroll)
      container.removeEventListener('touchmove', handleUserScroll)
    }
  }, [])

  // ============================================================================
  // Initial load / chat switch → scroll to bottom so user sees latest messages.
  // Uses a ref to track the previous messages identity (not just length).
  // ============================================================================
  const prevMsgIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (messages.length === 0) return
    // Detect chat switch by checking if the first message ID changed
    const firstMsgId = messages[0]?.id ?? null
    if (firstMsgId !== prevMsgIdRef.current) {
      prevMsgIdRef.current = firstMsgId
      const container = scrollContainerRef.current
      if (container) {
        // Use setTimeout to let the DOM render first
        setTimeout(() => {
          container.scrollTop = container.scrollHeight
        }, 100)
      }
      stickyRef.current = true
      setShowScrollBtn(false)
    }
  }, [messages])

  // ============================================================================
  // New message submitted → smooth scroll so user sees their bubble first.
  // The RAF loop then takes over for the thinking indicator + streaming.
  // ============================================================================
  const prevMsgCountRef = useRef(messages.length)
  useEffect(() => {
    if (messages.length > prevMsgCountRef.current) {
      const container = scrollContainerRef.current
      if (container) {
        // Small delay so the DOM paints the user bubble first, then scroll
        setTimeout(() => {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth',
          })
        }, 50)
      }
      stickyRef.current = true
      setShowScrollBtn(false)
    }
    prevMsgCountRef.current = messages.length
  }, [messages.length])

  // ============================================================================
  // When streaming completes → show ↓ button if content extends below viewport.
  // NO auto-scroll. The user clicks ↓ to go to bottom.
  // ============================================================================
  const prevStatusRef = useRef(status)
  useEffect(() => {
    const wasActive = prevStatusRef.current === 'streaming' || prevStatusRef.current === 'paused'
    const isDone = status === 'completed' || status === 'failed' || status === 'partial'

    if (wasActive && isDone) {
      const container = scrollContainerRef.current
      if (container) {
        // Check if content extends below the current viewport
        setTimeout(() => {
          const { scrollTop, scrollHeight, clientHeight } = container
          const distFromBottom = scrollHeight - scrollTop - clientHeight
          if (distFromBottom > 20) {
            // Not at bottom — show ↓ button so user can scroll down
            stickyRef.current = false
            setShowScrollBtn(true)
          }
        }, 200)
      }
    }
    prevStatusRef.current = status
  }, [status])

  // ============================================================================
  // ↓ button click → smooth scroll to bottom + re-engage sticky
  // ============================================================================
  const handleScrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    })
    stickyRef.current = true
    setShowScrollBtn(false)
  }, [])

  // Show empty state when no messages
  if (messages.length === 0 && status === 'idle') {
    return (
      <div className="flex-1 overflow-hidden">
        <EmptyState />
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-hidden relative">
      {/* Scrollable area */}
      <div ref={scrollContainerRef} className="h-full overflow-y-auto chat-scroll">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
          {messages.map((msg) => {
            if (msg.role === 'user') {
              return <UserMessageBubble key={msg.id} message={msg} />
            }
            const isLive = msg.id === currentMessageId
            return (
              <AssistantMessageBubble
                key={msg.id}
                message={msg}
                isLive={isLive}
                liveState={isLive ? liveState : undefined}
              />
            )
          })}

          {/* Scroll anchor */}
          <div ref={bottomRef} className="h-1" />
        </div>
      </div>

      {/* Floating ↓ button — absolute positioned at bottom center of wrapper */}
      {showScrollBtn && messages.length > 0 && (
        <ScrollToBottomButton onClick={handleScrollToBottom} />
      )}
    </div>
  )
}
